import { createContext, useContext, useMemo, useState } from "react";

// Hidden/visible state for the values inside each employee-profile section card.
// In view/edit mode every card starts hidden so a profile opened on a shared
// screen doesn't show PAN, bank or salary details at a glance; each card's eye
// button reveals just that card, and the page header's button reveals or hides
// them all. Create mode starts visible -- blurring a form you're filling in from
// scratch would only get in the way.
//
// Same shape as SectionCollapseContext: a fallback plus per-card overrides, so
// hide-all / view-all also covers cards that aren't listed anywhere.
export interface SectionPrivacyValue {
  isHidden: (id: string) => boolean;
  toggle: (id: string) => void;
  setAll: (hidden: boolean) => void;
  /** True only when no card is hidden -- drives the header toggle's label. */
  allVisible: boolean;
}

const SectionPrivacyContext = createContext<SectionPrivacyValue | null>(null);

interface PrivacyState {
  fallback: boolean;
  overrides: Record<string, boolean>;
}

export function useSectionPrivacyState(defaultHidden: boolean): SectionPrivacyValue {
  const [{ fallback, overrides }, setState] = useState<PrivacyState>({ fallback: defaultHidden, overrides: {} });
  return useMemo(() => ({
    isHidden: (id) => overrides[id] ?? fallback,
    toggle: (id) => setState(p => ({ ...p, overrides: { ...p.overrides, [id]: !(p.overrides[id] ?? p.fallback) } })),
    setAll: (hidden) => setState({ fallback: hidden, overrides: {} }),
    allVisible: !fallback && !Object.values(overrides).some(Boolean),
  }), [fallback, overrides]);
}

export const SectionPrivacyProvider = SectionPrivacyContext.Provider;

// Outside a provider (or in a test) nothing is hidden.
const ALWAYS_VISIBLE: SectionPrivacyValue = {
  isHidden: () => false, toggle: () => {}, setAll: () => {}, allVisible: true,
};

export function useSectionPrivacy(): SectionPrivacyValue {
  return useContext(SectionPrivacyContext) ?? ALWAYS_VISIBLE;
}
